import type { RequiredBy } from '@iconik-io/core/src/types';
import {
  type AssetsEditSegmentSchema,
  type AssetsHttpGetAssetsByAssetIdSegmentsQuery,
  type AssetsSegmentElasticSchemaWithId,
  type AssetsSegmentSchema,
  type AssetsSegmentSchemaWithId,
  type AssetsSegmentSchemaWithIdNullable,
  httpGetAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';
import type { FilesAttachmentSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/files';

import type { MetadataValues } from './metadata';

// TODO BE-4012: drop once the segment spec on test declares the comment
// attachments.
type SegmentAttachmentFields = {
  attachments?: FilesAttachmentSchemaWithIdNullable[];
};

// TODO BE-4811: drop this file once the segment spec declares its metadata
// shape and the ids the endpoints always send.
export type AssetsSegmentSchemaWithIdPatched = Omit<
  AssetsSegmentSchemaWithId,
  'metadata' | 'asset_id'
> &
  SegmentAttachmentFields & {
    metadata?: MetadataValues;
    asset_id: string;
  };

/**
 * The generated segment declares a loose metadata bag and an optional asset id,
 * so the patched shape is stated once here rather than at each caller. A create
 * response also types its own id as optional, though the endpoint always sends
 * one.
 */
export const asSegment = (
  body: AssetsSegmentSchemaWithId | AssetsSegmentSchema,
): AssetsSegmentSchemaWithIdPatched =>
  // biome-ignore lint/plugin: see above.
  body as AssetsSegmentSchemaWithIdPatched;

/**
 * The same widening for a list. Segments also arrive from the elastic and
 * nullable responses, whose metadata bag is loose and whose fields are
 * nullable, while the patched shape narrows both.
 */
export const asSegments = (
  objects:
    | AssetsSegmentSchemaWithIdNullable[]
    | Partial<AssetsSegmentElasticSchemaWithId>[],
): Partial<AssetsSegmentSchemaWithIdPatched>[] =>
  // biome-ignore lint/plugin: see asSegment.
  objects as Partial<AssetsSegmentSchemaWithIdPatched>[];

export type AssetsSegmentSchemaPatched = Omit<
  AssetsSegmentSchema,
  'metadata' | 'asset_id'
> &
  SegmentAttachmentFields & {
    metadata?: MetadataValues;
    asset_id: string;
  };

export type AssetsEditSegmentSchemaPatched = Omit<
  AssetsEditSegmentSchema,
  'metadata'
> & {
  metadata?: MetadataValues;
};

export type AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched =
  RequiredBy<Partial<AssetsSegmentSchemaWithIdPatched>, 'transcription_id'>;

/**
 * `segment_type` is generated as a single string, but the client serializes
 * arrays with `arrayFormat: 'repeat'` and the endpoint filters on every value
 * given.
 */
export type AssetsHttpGetAssetsByAssetIdSegmentsQueryPatched = Omit<
  AssetsHttpGetAssetsByAssetIdSegmentsQuery,
  'segment_type'
> & {
  segment_type?: string | string[];
};

export const getAssetSegmentsRequest = (
  assetId: string,
  query: AssetsHttpGetAssetsByAssetIdSegmentsQueryPatched,
) =>
  httpGetAssetsByAssetIdSegments(
    assetId,
    // biome-ignore lint/plugin: the generated endpoint still declares the single-string segment_type, see the type above.
    query as AssetsHttpGetAssetsByAssetIdSegmentsQuery,
  );
